import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { LanguageMenu } from '../components/LanguageMenu';
import { ThemeMenu } from '../components/ThemeMenu';
import { PublicShareGrid } from './PublicShareGrid';
import { PublicShareViewer } from './PublicShareViewer';
import { usePublicShare } from './use-public-share';

/**
 * Page d'un partage public (`/p/:token`, `/p/:token/:n`) — XPHOTO-31, ADR-0009.
 *
 * ── CE QU'ELLE NE MONTE PAS, ET POURQUOI C'EST LE SUJET ───────────────────────────
 *
 * Ni `AppChrome` (en-tête, fil d'Ariane, cloche de jobs, menu de compte), ni
 * `PrivateRoute`, ni le moindre appel portant un slot. Elle est montée en FRÈRE de
 * `/login` dans l'arbre de routes, jamais sous la zone authentifiée : c'est ce qui
 * garantit qu'une fonctionnalité ajoutée demain à la coquille de l'application
 * n'atterrira pas sur une page publique par héritage.
 *
 * Elle conserve en revanche le sélecteur de LANGUE et de THÈME. Un visiteur n'a pas de
 * compte, donc pas de préférence enregistrée : la langue vient de son navigateur
 * (`detectInitialLocale`) et le thème de son système — mais il doit pouvoir corriger
 * les deux, comme sur l'écran de connexion.
 *
 * ── L'ÉTAT « INTROUVABLE » EST DÉLIBÉRÉMENT VAGUE ─────────────────────────────────
 *
 * L'API rend le même 404 pour un jeton inconnu, révoqué, expiré ou dont le créateur est
 * parti. La page dit donc « ce lien n'est plus valable », sans jamais laisser deviner
 * lequel des quatre cas s'applique. Un message plus précis serait plus aimable et
 * renseignerait un inconnu sur l'existence du partage.
 */

/** Coquille commune aux états terminaux : centrée, sobre, sans chrome applicatif. */
function Centered({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface px-6 text-center text-fg">
      <div className="max-w-sm">{children}</div>
    </div>
  );
}

export function PublicShareRoute(): React.JSX.Element {
  const { t, tp } = useI18n();
  const navigate = useNavigate();
  const params = useParams<{ token: string; n?: string }>();
  const token = params.token ?? '';
  const state = usePublicShare(token);

  const openRank = useCallback(
    (rank: number) => navigate(`/p/${encodeURIComponent(token)}/${rank}`),
    [navigate, token],
  );
  const closeViewer = useCallback(
    () => navigate(`/p/${encodeURIComponent(token)}`),
    [navigate, token],
  );

  if (state.status === 'not-found') {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">{t('publicShare.goneTitle')}</h1>
        <p className="mt-2 text-sm text-muted">{t('publicShare.goneBody')}</p>
      </Centered>
    );
  }

  if (state.status === 'error') {
    return (
      <Centered>
        <h1 className="text-lg font-semibold">{t('publicShare.errorTitle')}</h1>
        <p className="mt-2 text-sm text-muted">{t('publicShare.errorBody')}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('publicShare.retry')}
        </button>
      </Centered>
    );
  }

  if (state.status === 'loading') {
    return (
      <Centered>
        <p className="text-sm text-muted" role="status">
          {t('publicShare.loading')}
        </p>
      </Centered>
    );
  }

  const { share, assets } = state;
  // Rang issu de l'URL : borné et validé. Un `/p/<jeton>/999` sur un album de 12
  // n'est pas une erreur à afficher, c'est un lien périmé — on retombe sur la grille.
  const parsedRank = params.n === undefined ? null : Number.parseInt(params.n, 10);
  const rank =
    parsedRank !== null &&
    Number.isInteger(parsedRank) &&
    parsedRank >= 1 &&
    parsedRank <= assets.length
      ? parsedRank
      : null;

  return (
    <div className="min-h-dvh bg-surface text-fg">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border px-4 py-6 sm:px-6">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold">{share.title}</h1>
          <p className="mt-1 text-sm tabular-nums text-muted">
            {tp('publicShare.count', share.total)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <LanguageMenu />
          <ThemeMenu />
        </div>
      </header>

      <main className="px-2 py-2 sm:px-3 sm:py-3">
        {assets.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <PhotoIcon className="h-10 w-10 text-muted" aria-hidden="true" />
            <p className="text-sm text-muted">{t('publicShare.empty')}</p>
          </div>
        ) : (
          <PublicShareGrid token={token} assets={assets} onOpen={openRank} />
        )}
      </main>

      {rank !== null && (
        <PublicShareViewer
          token={token}
          assets={assets}
          rank={rank}
          downloadable={share.shareOriginals}
          onClose={closeViewer}
          onNavigate={openRank}
        />
      )}
    </div>
  );
}
